import { getTouchPayload, type Attribution } from './attribution';
import { isAllowed, onConsentChange } from './consent';
import { postIdentify, postMarketingTouch, type ClickIds } from '../apis/attribution';
import { getAnonId } from './conversionTracking';

/**
 * Recording an arrival, first-party.
 *
 * `attribution.ts` captures what was on the landing URL into this browser's own
 * storage. This is what sends it somewhere the business can query — which is
 * the difference between knowing that a campaign parameter existed and being
 * able to say how many leads it produced.
 *
 * ## The three consent tiers, and why they differ
 *
 * The fields on a tagged URL are not one kind of data, so they do not get one
 * rule:
 *
 *  - **Campaign labels** (`utm_*`, the advertisement's own ids) are sent
 *    regardless of consent. They are not information about a person; they are
 *    information about our own marketing, which happens to have been observed
 *    on a page. This is the same reasoning `attribution.ts` already applies to
 *    storing them and `conversionTracking.ts` applies to the funnel.
 *
 *  - **The browser id** requires analytics consent, exactly as it does for the
 *    funnel. Without it the arrival is still recorded, just not joined to this
 *    browser's other activity.
 *
 *  - **Click identifiers** require advertising consent. A `gclid` is a
 *    pseudonymous identifier issued by an advertising company for one person's
 *    click, and its only use is telling that company what that person went on to
 *    do. Sending it to our own server is harmless; the reason it was sent is
 *    not, so it takes the consent that matches its purpose.
 *
 * A visitor who declines advertising is therefore still counted in every
 * campaign figure on the paid-ads dashboard, and is simply never reported back
 * to Meta or TikTok. That is the correct outcome in both directions.
 */

const CLICK_ID_FIELDS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid'] as const;

const LABEL_FIELDS: (keyof Attribution)[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'campaign_id',
  'adset_id',
  'ad_id',
  'creative_id',
];

/**
 * Once per page load, and only when the load is actually attributable.
 *
 * A second call in the same load would be a duplicate; the server collapses
 * repeats of the same campaign inside a thirty-minute window anyway, but not
 * making the request at all is cheaper than having it discarded.
 */
let sent = false;

/**
 * True when the arrival was recorded without its click identifiers because
 * advertising consent had not been given at the time.
 *
 * Under GDPR the default is a full denial until the visitor answers, so a
 * European visitor's landing is recorded with campaign labels only. If they then
 * accept, the click id becomes usable and the arrival is re-posted once — the
 * server recognises it as the same click and enriches the existing row rather
 * than writing a second one.
 */
let withheldClickIds = false;

export const recordTouch = (): void => {
  if (typeof window === 'undefined' || sent) return;

  const payload = getTouchPayload();
  if (!payload) return;

  sent = true;

  const attribution: Record<string, string> = {};
  LABEL_FIELDS.forEach((field) => {
    const value = payload[field];
    if (typeof value === 'string' && value) attribution[field] = value;
  });

  const clickIds: ClickIds = {};
  if (isAllowed('advertising')) {
    CLICK_ID_FIELDS.forEach((field) => {
      const value = payload[field];
      if (value) clickIds[field] = value;
    });
    withheldClickIds = false;
  } else {
    withheldClickIds = payload.hasClickId;
  }

  void postMarketingTouch({
    anonId: getAnonId(),
    attribution,
    clickIds: Object.keys(clickIds).length ? clickIds : undefined,
    landingPage: payload.landing_page,
    referrer: payload.referrer,
  });
};

/**
 * Links this browser's recorded arrivals to the account that just signed in.
 *
 * Called after sign-in and after registration — the two moments an anonymous
 * browser becomes a known person. Without it, somebody who clicked a TikTok
 * advertisement in March and opened an account in June is two unrelated rows,
 * and the purchase they make in August is reported as unattributed.
 */
export const identifyTouches = (): void => {
  if (typeof window === 'undefined') return;
  void postIdentify(getAnonId());
};

/**
 * Re-posts the arrival once, if advertising consent arrives after the landing
 * was already recorded.
 *
 * Registered at module load rather than from a component, because the landing
 * is recorded before React mounts and the banner can be answered at any point
 * afterwards. It fires at most once: the flag is cleared before the re-post, so
 * a visitor toggling consent repeatedly does not generate a row per toggle.
 */
onConsentChange(() => {
  if (!withheldClickIds || !isAllowed('advertising')) return;
  withheldClickIds = false;
  sent = false;
  recordTouch();
});

/**
 * Lets a fresh landing be recorded again after a client-side navigation that
 * genuinely represents a new arrival — following a nurture link into the app,
 * for instance. Nothing calls it yet; it exists so that the once-per-load guard
 * above is a decision rather than a limitation somebody has to work around.
 */
export const resetTouchGuard = (): void => {
  sent = false;
};
